import { format, parseISO } from 'date-fns';
import { dateLocaleFor } from '@/i18n';

// Constructing an Intl.NumberFormat is far costlier than formatting with it, and a long list
// formats hundreds of cells per render — so cache one formatter per (locale, currency).
const moneyFormatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(lng: string, currency: string): Intl.NumberFormat {
  const key = `${lng}|${currency}`;
  let formatter = moneyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(lng, { style: 'currency', currency, maximumFractionDigits: 2 });
    moneyFormatters.set(key, formatter);
  }
  return formatter;
}

/** A currency amount formatted for the active locale (e.g. "PLN 1,230.00"). */
export const formatMoney = (lng: string, amount: number, currency: string) =>
  moneyFormatter(lng, currency).format(amount);

/** An ISO date as "10 Jun 2026" in the active locale, or an em dash when it's missing. */
export const formatDate = (lng: string, iso: string | null | undefined) =>
  iso ? format(parseISO(iso), 'd MMM yyyy', { locale: dateLocaleFor(lng) }) : '—';

/** An ISO date-time as "10 Jun 2026, 09:30" in the active locale, or an em dash when it's missing. */
export const formatDateTime = (lng: string, iso: string | null | undefined) =>
  iso ? format(parseISO(iso), 'd MMM yyyy, HH:mm', { locale: dateLocaleFor(lng) }) : '—';

/**
 * A bank account number grouped for readability. A bare 26-digit Polish NRB is shown in its
 * conventional `61 1090 1014 0000 0712 1981 2874` layout (2 digits, then six groups of 4);
 * anything else (e.g. an IBAN like `PL61 1090 …`) falls back to plain groups of four.
 */
export const formatAccountFull = (account: string): string => {
  const compact = account.replace(/\s+/g, '');
  if (/^\d{26}$/.test(compact)) {
    return compact.replace(
      /^(\d{2})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})(\d{4})$/,
      '$1 $2 $3 $4 $5 $6 $7',
    );
  }
  return compact.match(/.{1,4}/g)?.join(' ') ?? compact;
};
